/**
 * Quest Navigation Helper
 *
 * Maps quest objective types to navigation targets so players
 * can jump directly to the relevant game location.
 */

import { QuestDefinition, ActiveQuest, QuestObjectiveType } from '@/types';

export interface QuestNavigationTarget {
  tab: 'fishing' | 'inventory' | 'outfitters' | 'markets' | 'buddies' | 'stats';
  label: string;
  icon: string;
  /** For SELL_FISH objectives — the quest ID to open sell panel */
  questId?: string;
}

/**
 * Determine which tab to navigate to based on the first incomplete quest objective.
 * Returns null if all objectives are complete (turn-in state, no navigation needed).
 */
export function getQuestNavigationTarget(
  definition: QuestDefinition,
  activeQuest: ActiveQuest
): QuestNavigationTarget | null {
  // Find first incomplete objective
  const incompleteIndex = activeQuest.objectives.findIndex((obj) => !obj.completed);
  if (incompleteIndex === -1) return null; // All objectives complete

  const objectiveDef = definition.objectives[incompleteIndex];
  if (!objectiveDef) return null;

  switch (objectiveDef.type) {
    case QuestObjectiveType.CATCH_FISH:
      return { tab: 'fishing', label: 'Go Fish', icon: '🎣' };

    case QuestObjectiveType.SELL_FISH:
      return { tab: 'markets', label: 'Go Sell', icon: '🍽️', questId: activeQuest.questId };

    case QuestObjectiveType.HIRE_BUDDY:
      return { tab: 'buddies', label: 'Hire Buddy', icon: '🧑‍🤝‍🧑' };

    case QuestObjectiveType.BUY_EQUIPMENT:
    case QuestObjectiveType.UNLOCK_ITEM:
      return { tab: 'outfitters', label: 'Go Shop', icon: '🏪' };

    case QuestObjectiveType.EARN_GOLD:
    case QuestObjectiveType.EARN_CURRENCY:
      return { tab: 'markets', label: 'Sell Fish', icon: '💰' };

    case QuestObjectiveType.BUDDY_CATCH_FISH:
      return { tab: 'buddies', label: 'View Buddies', icon: '🤝' };

    case QuestObjectiveType.HAULER_SELL_FISH:
    case QuestObjectiveType.HAULER_EARN_GOLD:
      return { tab: 'buddies', label: 'View Haulers', icon: '🚚' };

    default:
      return null;
  }
}
